import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { CalendarIcon, ClockIcon, UserGroupIcon, DocumentTextIcon, BellIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { parseTimeToDate, normalizeTimeDate, calculateTimeDuration, formatTimeForApi } from '../../utils/time';

const SiteVisitModal = ({ isOpen, onClose, onSubmit, type, data, assignedTechs = [], isEdit = false, editData = null }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  let  id = isEdit ? (editData?.id ?? data?.id) : undefined;
  const customStyles = {
    option: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
    singleValue: (provided, state) => ({
      ...provided,
      opacity: state.data.value === '' ? 0.5 : 1,
      fontStyle: state.data.value === '' ? 'normal' : 'normal',
    }),
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
    trigger
  } = useForm({
    defaultValues: {
      start_date: '',
      arrival_start: '',
      arrival_end: '',
      duration_h: '',
      duration_m: '',
      assigned_tech_ids: [],
      note_for_tech: '',
      notify_all_techs: false,
      id: id,
    }
  });

  useEffect(() => {
    if (isOpen) {
      if (isEdit && editData) {
        setValue('start_date', editData.onsite_visit_date || editData.start_date || '');
        setValue('arrival_start', parseTimeToDate(editData.arrival_start));
        setValue('arrival_end', parseTimeToDate(editData.arrival_end));
        setValue('duration_h', editData.duration_h || '');
        setValue('duration_m', editData.duration_m || '');
        setValue('assigned_tech_ids', editData.assigned_tech_ids || []);
        setValue('note_for_tech', editData.note_for_tech || '');
        setValue('notify_all_techs', editData.notify_all_techs || false);
        setValue('id', editData.id || '');
      } else if (data) {
        if (data.onsite_visit_date) {
          setValue('start_date', data.onsite_visit_date);
        }
        if (data?.arrival_start) {
          setValue('arrival_start', parseTimeToDate(data.arrival_start));
        }
        if (data?.arrival_end) {
          setValue('arrival_end', parseTimeToDate(data.arrival_end));
        }
        if (data?.assigned_tech_ids) {
          setValue('assigned_tech_ids', data.assigned_tech_ids);
        }
        if (data?.note_for_tech) {
          setValue('note_for_tech', data.note_for_tech);
        }
        if (data?.notify_all_techs) {
          setValue('notify_all_techs', data.notify_all_techs);
        }
        if(data?.duration_h){
          setValue('duration_h', data.duration_h);
        }
        if(data?.duration_m){
          setValue('duration_m', data.duration_m);
        }
        if(data?.id){
          setValue('id',data?.id);
        }
      }
    }
  }, [isOpen, data, editData, isEdit, setValue]);

  const arrivalStart = watch('arrival_start');
  const arrivalEnd = watch('arrival_end');

  useEffect(() => {
    const duration = calculateTimeDuration(arrivalStart, arrivalEnd);
    if (!duration) {
      setValue('duration_h', '');
      setValue('duration_m', '');
      return;
    }

    setValue('duration_h', duration.hours.toString());
    setValue('duration_m', duration.minutes.toString());
  }, [arrivalStart, arrivalEnd, setValue]);

  const handleFormSubmit = async (formData) => {
    setIsSubmitting(true);
    try {
     
      // Combine form data with original estimation data
      const combinedData = {
        ...formData,
        onsite_visit_date: formData.start_date,
        arrival_start: formatTimeForApi(formData.arrival_start),
        arrival_end: formatTimeForApi(formData.arrival_end),
        duration_h: formData.duration_h,
        duration_m: formData.duration_m,
        assigned_tech_ids: formData.assigned_tech_ids,
        note_for_tech: formData.note_for_tech,
        notify_all_techs: formData.notify_all_techs,
        edit: isEdit,
        // Preserve the site visit ID during edits, don't override with estimation data
        id: id,
 
      };
       
      await onSubmit(combinedData);
      reset();
      setIsSubmitting(false);
      onClose();
    } catch (error) {
      console.error('Error submitting site visit data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <CalendarIcon className="h-6 w-6 text-[#009efb] mr-3" />
            <h2 className="text-xl font-semibold text-gray-900">
              {isEdit ? 'Edit Site Visit' : 'Add New Site Visit'} - {type === 'estimation' ? 'Estimation' : 'Job'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Start Date */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <CalendarIcon className="h-4 w-4 mr-2" />
                Date
              </label>
              <input
                type="text"
                placeholder='Enter Date'
                {...register("start_date", { required: "Date is required" })}
                className={`datepicker w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] transition-colors ${
                  errors.start_date ? "border-red-500" : ""
                }`}
                onInput={(e) => setValue("start_date", e.target.value || "")}
              />
              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
              )}
            </div>

            {/* Arrival Start */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <ClockIcon className="h-4 w-4 mr-2" />
                Arrival Start
              </label>
              <DatePicker
                selected={watch('arrival_start') instanceof Date ? watch('arrival_start') : null}
                onChange={(date) => setValue('arrival_start', normalizeTimeDate(date), { shouldValidate: true })}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="h:mm aa"
                wrapperClassName="w-full"
                placeholderText="Select Start Time"
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                  errors.arrival_start ? "border-red-500" : ""
                }`}
              />
              {errors.arrival_start && (
                <p className="mt-1 text-sm text-red-600">{errors.arrival_start.message}</p>
              )}
            </div>

            {/* Arrival End */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <ClockIcon className="h-4 w-4 mr-2" />
                Arrival End
              </label>
              <DatePicker
                selected={watch('arrival_end') instanceof Date ? watch('arrival_end') : null}
                onChange={(date) => setValue('arrival_end', normalizeTimeDate(date), { shouldValidate: true })}
                showTimeSelect
                showTimeSelectOnly
                timeIntervals={30}
                timeCaption="Time"
                dateFormat="h:mm aa"
                wrapperClassName="w-full"
                placeholderText="Select End Time"
                className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                  errors.arrival_end ? "border-red-500" : ""
                }`}
              />
              {errors.arrival_end && (
                <p className="mt-1 text-sm text-red-600">{errors.arrival_end.message}</p>
              )}
            </div>

            {/* Estimated Duration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Estimated Duration
              </label>
              <div className="flex gap-2">
                
                {/* Hours input */}
                <div className="w-1/2">
                  <input
                    type="number"
                    {...register("duration_h", { min: 0, max: 24, required: "Hours is required" })}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                      errors.duration_h ? "border-red-500" : ""
                    }`}
                    placeholder="Hours"
                    min="0"
                    max="24"
                  />
                  {errors.duration_h && (
                    <p className="mt-1 text-sm text-red-600">{errors.duration_h.message}</p>
                  )}
                  
                </div>

                {/* Minutes input */}
                <div className="w-1/2">
                  <input
                    type="number"
                    {...register("duration_m", { min: 0, max: 59, required: "Minutes is required" })}
                    className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb] ${
                      errors.duration_m ? "border-red-500" : ""
                    }`}
                    placeholder="Minutes"
                    min="0"
                    max="59"
                  />
                  {errors.duration_m && (
                    <p className="mt-1 text-sm text-red-600">{errors.duration_m.message}</p>
                  )}
                </div>

              </div>
            </div>
 
            {/* Assigned Techs */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <UserGroupIcon className="h-4 w-4 mr-2" />
                Assigned Technicians
              </label>
              <Select
              {...register("assigned_tech_ids", { required: "Technicians are required" })}
                className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                  errors.assigned_tech_ids ? 'border-red-500' : ''
                }`}
                isMulti
                isSearchable
                options={
                  assignedTechs?.map(tech => ({
                    value: tech.id,
                    label: tech.first_name + ' ' + tech.last_name
                  })) || []
                }
                value={
                  (watch('assigned_tech_ids') || []).map(id => {
                    const tech = assignedTechs?.find(t => t.id === id);
                    return tech ? { value: tech.id, label: tech.first_name + ' ' + tech.last_name } : null;
                  }).filter(Boolean)
                }
                onChange={(selectedOptions) => {
                  const selectedIds = selectedOptions
                    ? selectedOptions.map(option => option.value)
                    : [];
                    
                  setValue('assigned_tech_ids', selectedIds);
                  trigger('assigned_tech_ids');
                }}
                styles={customStyles}
                placeholder="Click to select techs"
                classNamePrefix="react-select"
              />
              {errors.assigned_tech_ids && (
                <p className="mt-1 text-sm text-red-600">{errors.assigned_tech_ids.message}</p>
              )}
            </div>

            {/* Notes For Techs */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                Notes For Technicians
              </label>
              <textarea
                {...register("note_for_tech")}
                rows="3"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009efb] focus:border-[#009efb]"
                placeholder="Enter specific instructions, safety notes, or special requirements for technicians..."
              />
            </div>

            {/* Notifications */}
            <div className="md:col-span-2 flex items-center gap-2">
              <input
                type="checkbox"
                {...register("notify_all_techs")}
                className="h-4 w-4 text-[#009efb] border-gray-300 rounded focus:ring-[#009efb]"
              />
              <label className="text-sm text-gray-700 flex items-center">
                <BellIcon className="h-4 w-4 mr-2" />
                Notify all assigned technicians
              </label>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit(handleFormSubmit)}
              className="px-6 py-2 bg-[#009efb] text-white rounded-lg hover:bg-[#0089db] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Processing...' : (isEdit ? 'Update Site Visit' : 'Add Site Visit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SiteVisitModal;
